import React, { useRef, useState } from 'react';
import { getBoundData } from '../chartDataBindings';
import { TpChartProps } from '../interfaces';
import styles from './facetCategorical.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChartFilterButton, generateChartTitle, getHighlightedText, smartCountDisplayString } from '../../helpers/misc';
import useResizeObserver from '../../hooks/resize-observer';
import { CATEGORICAL_ORDERS, TpCategoryInfo } from '../../helpers/categoricalSorters';
import { createCategories, getCategoriesAsBlob } from '../../helpers/categoricalTools';
import SearchField from '../../../../components/search-field/SearchField';
import { TpEnSelectionFilterTypes } from '../../../../data/selection/interfaces';
import { getCategoricalEnFilterStep } from '../../../../data/selection/selectionEnFilterCategorical';
import { getSelectionHighlightColor } from '../../../themes/themes';
import { downloadBlobAsFile } from '../../helpers/downloadHelpers';
import { useExportFilename } from '../../hooks/useExportFilename';

export function FacetCategorical({ chartTypeManifest, dataContext, dataBinding, config, widgetContext }: TpChartProps) {
  const [searchString, setSearchString] = useState('');
  const [pageRowCount, setPageRowCount] = useState(0);
  const [pageOffset, setPageOffset] = useState(0);

  const refTableWrapper = useRef<HTMLDivElement>(null);
  const { getFilename } = useExportFilename();

  useResizeObserver(refTableWrapper, (info: any) => {
    if (!refTableWrapper.current) return;
    const clientHeight = refTableWrapper.current.clientHeight;
    const rowCount = Math.floor((clientHeight - 5) / 28);
    setPageRowCount(rowCount);
  });

  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);
  const colInfo = boundData.bindings.categories.columnInfo;
  const catData = boundData.bindings.categories.data;
  const itemIdData = boundData.bindings.itemId.data;

  const filterInfo = getCategoricalEnFilterStep(
    boundData.canonicalTypeInfo.selectionFilter,
    boundData.dataTable.id,
    colInfo.id,
  );

  const sortBy = config.sortBy ?? CATEGORICAL_ORDERS.Count; // Default sort order
  const categoriesMapping = createCategories(boundData.canonicalTypeInfo, catData, colInfo, null, itemIdData, sortBy);
  const categoriesData = categoriesMapping.categories;

  const downloadCategoriesTable = () => {
    const blobData = getCategoriesAsBlob(categoriesData);
    const fileName = getFilename(
      {
        assetId: dataContext.assetId,
        dataType: chartTypeManifest.id,
        title,
      },
      'csv',
    );
    downloadBlobAsFile(blobData, fileName);
  };

  if (widgetContext.setBitmapDownloadFunction) widgetContext.setBitmapDownloadFunction(downloadCategoriesTable);

  function onItemClicked(selectedCategoryId: any, exclude: boolean) {
    widgetContext.handleUserSelectionAction({
      type: TpEnSelectionFilterTypes.CATEGORICAL,
      dataTableId: boundData.dataTable.id,
      colId: colInfo.id,
      selectedCategoryId,
      exclude,
    });
  }

  let endOfPageOffset = 0;
  let displayedCount = 0;
  let rows = [];
  let maxCount = 1;

  for (let catIdx = 0; catIdx < categoriesData.length; catIdx++) {
    const cat = categoriesData[catIdx];
    maxCount = Math.max(maxCount, cat.count);
    let inMatch = true;
    if (searchString) {
      inMatch = false;
      if (cat.displayString.toLowerCase().indexOf(searchString.toLowerCase()) >= 0) inMatch = true;
    }
    if (inMatch) {
      if (displayedCount >= pageOffset && displayedCount < pageOffset + pageRowCount) {
        endOfPageOffset = displayedCount;
        rows.push(cat);
      }
      displayedCount++;
    }
  }

  rows = rows.map((row: TpCategoryInfo, rowIdx: number) => (
    <div
      key={`rw_${rowIdx}`}
      className={styles.categoryLine}
      onClick={() => {
        onItemClicked(row.value, false);
      }}
    >
      <div className={styles.categoryText}>
        {row.countSelected < row.count && (
          <div className={styles.categoryBarUnSelected} style={{ width: `${(row.count / maxCount) * 100}%` }} />
        )}
        <div className={styles.categoryBarSelected} style={{ width: `${(row.countSelected / maxCount) * 100}%` }} />
        <div className={styles.categoryBarLabel}>{getHighlightedText(row.displayString, searchString)}</div>
        {filterInfo && filterInfo.selectedCategoryIdList.includes(row.value) && (
          <div
            className={styles.filterSelectionIndicator}
            style={{ background: getSelectionHighlightColor().lighten(0.25).toCanvasString() }}
          />
        )}
      </div>
      <div className={styles.categoryCount}>{smartCountDisplayString(row.count)}</div>
      {filterInfo && filterInfo.excludedCategoryIdList.includes(row.value) && (
        <div
          className={styles.excludeFilterSelectionIndicator}
          style={{ color: getSelectionHighlightColor().lighten(0.25).toCanvasString() }}
        >
          <FontAwesomeIcon icon="times" />
        </div>
      )}
      <div
        className={styles.categoryExcludeButton}
        onClick={(ev) => {
          ev.stopPropagation();
          onItemClicked(row.value, true);
        }}
      >
        <FontAwesomeIcon icon="times" />
      </div>
    </div>
  ));

  const title = generateChartTitle(dataContext, dataBinding, config, colInfo.name);

  const isSimple = config.simpleLayout;
  return (
    <>
      <div className={styles.header}>{title}</div>
      <div
        className={styles.tableWrapper}
        ref={refTableWrapper}
        style={{ height: isSimple ? 'calc(100% - 50px)' : undefined }}
      >
        {rows}
      </div>
      {!isSimple && (
        <div className={styles.footer}>
          <div style={{ width: 'calc(100% - 110px)' }}>
            <SearchField
              updateSearchString={(newVal: string) => {
                setSearchString(newVal);
                setPageOffset(0);
              }}
            />
          </div>
          <div className={styles.pager}>
            {pageOffset > 0 && (
              <button onClick={() => setPageOffset(Math.max(0, pageOffset - pageRowCount))}>
                <FontAwesomeIcon icon="caret-left" />
              </button>
            )}
            {pageOffset + 1}-{endOfPageOffset + 1}/{smartCountDisplayString(displayedCount)}
            {endOfPageOffset + 1 < displayedCount && (
              <button onClick={() => setPageOffset(pageOffset + pageRowCount)}>
                <FontAwesomeIcon icon="caret-right" />
              </button>
            )}
          </div>
        </div>
      )}
      {filterInfo && <ChartFilterButton />}
    </>
  );
}
